import { useState } from 'react';
import { IAccreditationApplication } from 'interfaces/IAccreditationApplication';
import { useAccreditationApplications } from './useAccreditationApplications';
import IAccreditationApplicationForm from '../interfaces/IAccreditationApplicationForm';
import { LockOwner } from '../graphql/__generated__/graphql';

const checkLocalStorage = (identifier?: string) => {
    if (!identifier) return;
    const backup = localStorage.getItem(identifier);

    if (!backup) return null;
    try {
        return JSON.parse(backup);
    } catch (e) {
        // return null;
    }
};

export const useAccreditationApplication = (
    accreditationApplicationMaybe: IAccreditationApplication
) => {
    const {
        operations: { fetchApplications, upsertApplication, submitApplication },
    } = useAccreditationApplications();
    const [updating, setUpdating] = useState(false);

    const accreditationApplication: IAccreditationApplication =
        checkLocalStorage(accreditationApplicationMaybe.accreditationApplicationId) ||
        accreditationApplicationMaybe;

    const [application, setApplication] =
        useState<IAccreditationApplication>(accreditationApplication);

    const handleSubmitApplication = async () => {
        if (updating) return;

        setUpdating(true);

        return new Promise<boolean>(async (resolve, reject) => {
            submitApplication(application, LockOwner.Supervisor)
                .then((submittedApplication) => {
                    setApplication(submittedApplication);
                    resolve(true);
                })
                .catch((err) => {
                    console.error('An error occurred while submitting application', err);
                    reject(err);
                })
                .finally(() => setUpdating(false));
        });
    };

    const handleFetchApplication = async () => {
        setUpdating(true);
        fetchApplications()
            .then((applications: IAccreditationApplication[]) => {
                const updatedApplication = applications.find(
                    (a) => a.accreditationApplicationId === application.accreditationApplicationId
                );
                if (updatedApplication) setApplication(updatedApplication);
            })
            .finally(() => setUpdating(false));
    };

    const handleSaveProgress = async (formData: IAccreditationApplicationForm) => {
        if (updating) return;

        setUpdating(true);

        const updatedApplication: IAccreditationApplication = {
            ...application,
            jsonData: JSON.stringify(formData),
        };

        return new Promise<string | void>((resolve, reject) => {
            upsertApplication(updatedApplication)
                .then((application: IAccreditationApplication) => {
                    application.accreditationApplicationId &&
                        localStorage.removeItem(application.accreditationApplicationId);

                    setApplication((prevState) => ({
                        ...prevState,
                        ...application,
                    }));
                    resolve(application.accreditationApplicationId);
                })
                .catch((error) => {
                    console.error('An error occurred saving the application', error);
                    updatedApplication.accreditationApplicationId &&
                        localStorage.setItem(
                            updatedApplication.accreditationApplicationId,
                            JSON.stringify(updatedApplication)
                        );

                    reject(error);
                })
                .finally(() => setUpdating(false));
        });
    };

    return {
        models: {
            application,
            updating,
        },
        operations: {
            handleSaveProgress,
            handleSubmitApplication,
            handleFetchApplication,
        },
    };
};
